import type {
  Page,
  PageContentBlock,
  PageContentBlocksPageContentSlider,
  PageContentBlocksPageContentLinkCol,
  PageContentBlocksPageContentFeature,
  PageContentBlocksPageContentGeneric,
  PageContentBlocksPageContentFaq
} from '@branditdev/mf-types'

export type SliceStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'init'
export type SliceError = {
  action?: string
  message?: string
}
export type PortalPage = Omit<Page, 'page_content_blocks'> & {
  page_content_blocks: PageContentBlock[]
}

type SliderBlock = Omit<PageContentBlock, 'content_type' | 'child_content'> & {
  content_type: 'Slider'
  child_content: PageContentBlocksPageContentSlider
}

type LinkColumnBlock = Omit<PageContentBlock, 'content_type' | 'child_content'> & {
  content_type: 'LinkColumn'
  child_content: PageContentBlocksPageContentLinkCol
}

type FeatureBlock = Omit<PageContentBlock, 'content_type' | 'child_content'> & {
  content_type: 'Feature'
  child_content: PageContentBlocksPageContentFeature
}

type GenericBlockType = Omit<PageContentBlock, 'content_type' | 'child_content'> & {
  content_type: 'Generic'
  child_content: PageContentBlocksPageContentGeneric
}

type FaqBlockType = Omit<PageContentBlock, 'content_type' | 'child_content'> & {
  content_type: 'FAQ'
  child_content: PageContentBlocksPageContentFaq
}

export type RenderablePageContentBlock =
  | SliderBlock
  | LinkColumnBlock
  | FeatureBlock
  | GenericBlockType
  | FaqBlockType

const renderableContentTypes = new Set(['Slider', 'LinkColumn', 'Feature', 'Generic', 'FAQ'])

export const isRenderablePageContentBlock = (
  block: PageContentBlock
): block is RenderablePageContentBlock =>
  Boolean(block.visible) &&
  block.child_content !== null &&
  renderableContentTypes.has(block.content_type || '')

export type PortalPageEntry = {
  pageId: number
  page: PortalPage
}

export type PortalPagesData = Record<number, PortalPageEntry>
export type PortalPagesSlice = {
  status: SliceStatus
  errors: SliceError[]
  data: PortalPagesData
}
export const initialPortalPagesState: PortalPagesSlice = {
  status: 'init',
  errors: [],
  data: {}
}
export enum PORTAL_PAGES_ACTIONS {
  SET_PORTAL_PAGES_STATUS = 'SET_PORTAL_PAGES_STATUS',
  SET_PORTAL_PAGES_ERRORS = 'SET_PORTAL_PAGES_ERRORS',
  SET_PORTAL_PAGE_DATA = 'SET_PORTAL_PAGE_DATA',
  CLEAR_PORTAL_PAGES_ERRORS = 'CLEAR_PORTAL_PAGES_ERRORS',
  CLEAR_PORTAL_PAGES_DATA = 'CLEAR_PORTAL_PAGES_DATA'
}
export type PortalPagesStatusAction = {
  type: PORTAL_PAGES_ACTIONS.SET_PORTAL_PAGES_STATUS
  payload: PortalPagesSlice['status']
}
export type PortalPagesErrorsAction = {
  type: PORTAL_PAGES_ACTIONS.SET_PORTAL_PAGES_ERRORS
  payload: SliceError
}
export type PortalPageDataAction = {
  type: PORTAL_PAGES_ACTIONS.SET_PORTAL_PAGE_DATA
  payload: PortalPageEntry
}
export type PortalPagesClearAction = {
  type: PORTAL_PAGES_ACTIONS.CLEAR_PORTAL_PAGES_ERRORS | PORTAL_PAGES_ACTIONS.CLEAR_PORTAL_PAGES_DATA
}
export type PortalPagesAction =
  | PortalPagesStatusAction
  | PortalPagesErrorsAction
  | PortalPageDataAction
  | PortalPagesClearAction
